/**
 * Admin Portal → Partners → a partner → Links & tags: the tag list, a new tag, and the
 * tag detail — its /go link and destinations, the clipboard switch, the typed offer
 * (trial 7/14/30, monthly and annual discount levels, how long the monthly discount lasts —
 * 1, 3, 6 or 12 months, Codes PR-2 —, monthly and annual bounties), its status, its click and
 * attribution counts, its codes, and its website code (C7). A discount with no live store offer
 * on some product carries a WARNING (the list and the tag): the redeem card would promise a
 * discount the person can't buy there.
 * Spec: docs/handoff/PARTNERS_ADMIN_FE_HANDOFF.md §2.4 + "Offers and codes".
 *
 * Web-only (the admin SPA).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import {
  DEFAULT_MONTHLY_TERM,
  comboMissing,
  coverageWarning,
  defaultMonthlyTerm,
  monthlyTermChoices,
  monthlyTermError,
  shortTermNotice,
  termChoiceLabel,
  type DiscountCoverage,
  type DiscountOffer,
} from '../../../components/admin/discountTermsLogic';
import {
  BOUNTY_FORWARD_NOTE,
  CLIPBOARD_HANDOFF_NOTE,
  DEFAULT_BOUNTY_ANNUAL_INPUT,
  DEFAULT_BOUNTY_MONTHLY_INPUT,
  DEFAULT_PARTNER_TRIAL_DAYS,
  FUNNEL_STAGES,
  FUNNEL_STAGE_LABELS,
  LINK_DESTINATIONS,
  TAG_RE,
  TRIAL_CHOICES,
  accountsWithoutClickText,
  bountyLabel,
  discountChoices,
  formatCents,
  goLink,
  offerFormFromTag,
  offerKindLabel,
  offerPatch,
  parseDollarsToCents,
  statusBadge,
  statusMoveCopy,
  tagEditable,
  tagStatusMoves,
  trialLabel,
  type DiscountLevel,
  type Funnel,
  type LinkRow,
  type OfferForm,
  type PartnerStatus,
  type TagRow,
} from './partnersLogic';
import { PartnerCodesPanel, TagCodeMaker } from './PartnerCodesPanel';
import { CodesAdmin } from '../codes/codesAdminService';
import { houseTagBody } from '../codes/codesAdminLogic';
import { PartnerWebsiteCode } from './PartnerWebsiteCode';
import { PartnerEmailKit } from './PartnerEmailKit';
import { PartnerTagSendMode } from './PartnerTagSendMode';
import { PartnerWebhookCard } from './PartnerWebhookCard';
import {
  NoticeLine,
  StatusBadge,
  TypedConfirm,
  copyText,
  errText,
  fmtDate,
  hint,
  input,
  label,
  row,
  td,
  th,
  useBusy,
  type Notice,
} from './PartnersShared';

export const PartnerTagPanel: React.FC<{
  partnerId: string;
  partnerCode: string;
  partnerStatus: PartnerStatus;
  judgedAtTap: boolean | null;
  tags: TagRow[];
  links: LinkRow[];
  selectedTagId: string | null;
  onSelect: (tagId: string | null) => void;
  writable: boolean;
  endedTerminal: boolean;
  onChanged: () => void;
  /** The Coach Cedar house partner (migration 1876): no bounty anywhere — a house tag pays none. */
  house?: boolean;
  /** The partner's display name, for the email kit (partner launch UC3); the code when absent. */
  partnerName?: string;
}> = ({ partnerId, partnerCode, partnerStatus, judgedAtTap, tags, links, selectedTagId, onSelect, writable, endedTerminal, onChanged, house = false, partnerName }) => {
  const partnerEnded = partnerStatus === 'ended';
  const [levels, setLevels] = useState<DiscountLevel[]>([]);
  const [offers, setOffers] = useState<DiscountOffer[]>([]);
  const [coverage, setCoverage] = useState<DiscountCoverage | undefined>(undefined);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    PartnersAdmin.discountLevels().then((r) => setLevels(r.levels || [])).catch(() => setLevels([]));
    PartnersAdmin.discountOffers().then((r) => setOffers(r.offers || [])).catch(() => setOffers([]));
  }, []);
  // Which store products can sell each live tag's discount — re-read whenever the tags change.
  const tagsKey = tags.map((t) => `${t.id}:${t.discount_monthly_pct}:${t.discount_monthly_months}:${t.discount_annual_pct}:${t.status}`).join('|');
  useEffect(() => {
    PartnersAdmin.discountCoverage({ partnerId }).then(setCoverage).catch(() => setCoverage(undefined));
  }, [partnerId, tagsKey]);
  const unbuyable = new Set((coverage?.tags ?? []).filter((t) => !t.buyable_everywhere).map((t) => t.tag_id));

  const selected = tags.find((t) => t.id === selectedTagId) ?? null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminCard flush>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Tag</th><th style={th}>Name</th><th style={th}>Status</th><th style={th}>Offer</th>
              {!house && <th style={th}>Bounty</th>}<th style={th}>Clipboard</th><th style={th}>Created</th>
            </tr>
          </thead>
          <tbody>
            {tags.length === 0 && <tr><td style={td} colSpan={house ? 6 : 7}>No tags yet.</td></tr>}
            {tags.map((t) => (
              <tr key={t.id} style={{ background: t.id === selectedTagId ? 'var(--surface-subtle, #f9fafb)' : undefined, cursor: 'pointer' }}
                onClick={() => onSelect(t.id === selectedTagId ? null : t.id)}>
                <td style={td}><b>{t.tag}</b></td>
                <td style={td}>{t.name}</td>
                <td style={td}><StatusBadge status={t.status} tone={statusBadge(t.status)} /></td>
                <td style={td}>
                  {offerKindLabel(t.offer_kind)}<div style={hint}>{t.offer_summary}</div>
                  {unbuyable.has(t.id) && (
                    <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }} data-testid={`tag-unbuyable-${t.tag}`}>
                      ⚠ No live store offer on some products — open the tag for details.
                    </div>
                  )}
                </td>
                {!house && <td style={td}>{bountyLabel(t)}</td>}
                <td style={td}>{t.clipboard_handoff ? 'On' : 'Off'}</td>
                <td style={td}>{fmtDate(t.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminCard>

      {writable && !partnerEnded && (
        <div>
          <AdminButton size="sm" variant="secondary" onClick={() => setShowNew((v) => !v)}>{showNew ? 'Close' : 'New tag'}</AdminButton>
        </div>
      )}
      {showNew && writable && (
        <NewTagForm partnerId={partnerId} levels={levels} offers={offers} coverage={coverage} house={house}
          onCreated={(id) => { setShowNew(false); onChanged(); onSelect(id); }} />
      )}

      {selected && (
        <TagDetail
          key={selected.id}
          tag={selected}
          partnerId={partnerId}
          partnerCode={partnerCode}
          tags={tags}
          links={links.filter((l) => l.tag_id === selected.id)}
          levels={levels}
          offers={offers}
          coverage={coverage}
          writable={writable}
          endedTerminal={endedTerminal}
          partnerStatus={partnerStatus}
          judgedAtTap={judgedAtTap}
          onChanged={onChanged}
          house={house}
          partnerName={partnerName ?? partnerCode}
        />
      )}
    </div>
  );
};

// ── new tag ──────────────────────────────────────────────────────────────────

/** The warnings for an offer being edited: each period whose (discount, length) has no live store offer somewhere. */
export function offerCoverageWarnings(coverage: DiscountCoverage | undefined, monthlyPct: number | null,
  monthlyMonths: number | null, annualPct: number | null): string[] {
  if (!coverage) return [];
  const out: Array<string | null> = [];
  if (monthlyPct != null) {
    out.push(coverageWarning('monthly', monthlyPct, monthlyMonths, comboMissing(coverage, 'monthly', monthlyPct, monthlyMonths)));
  }
  if (annualPct != null) {
    out.push(coverageWarning('annual', annualPct, 12, comboMissing(coverage, 'annual', annualPct, 12)));
  }
  return out.filter((w): w is string => !!w);
}

const CoverageWarningLines: React.FC<{ lines: string[]; testId: string }> = ({ lines, testId }) =>
  lines.length ? (
    <div data-testid={testId} style={{ margin: '8px 0', padding: '8px 10px', borderRadius: 6,
      background: 'var(--status-warning-bg, #fffbeb)', border: '1px solid var(--status-warning-border, #fcd34d)' }}>
      {lines.map((l) => <div key={l} style={{ fontSize: 13, color: 'var(--status-warning-text, #92400e)' }}>⚠ {l}</div>)}
    </div>
  ) : null;

/** The old-build notice under an offer whose monthly discount lasts less than 12 months (L-D). */
const ShortTermNotice: React.FC<{ months: number | null; testId: string }> = ({ months, testId }) => {
  const notice = shortTermNotice(months);
  return notice ? <div data-testid={testId} style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>{notice}</div> : null;
};

const NewTagForm: React.FC<{ partnerId: string; levels: DiscountLevel[]; offers: DiscountOffer[];
  coverage: DiscountCoverage | undefined; onCreated: (tagId: string) => void; house?: boolean }> = ({ partnerId, levels, offers, coverage, onCreated, house = false }) => {
  // A partner tag starts at the launch defaults (a free month, CAD 10 / CAD 30); a house tag at 7 days, no bounty.
  const [f, setF] = useState({
    tag: '', name: '', bm: house ? '0' : DEFAULT_BOUNTY_MONTHLY_INPUT, ba: house ? '' : DEFAULT_BOUNTY_ANNUAL_INPUT,
    trial: house ? 7 : DEFAULT_PARTNER_TRIAL_DAYS, dm: '', dmm: String(DEFAULT_MONTHLY_TERM), da: '',
  });
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const choices = discountChoices(levels, null);
  const newTermChoices = monthlyTermChoices(offers, f.dm ? Number(f.dm) : null);

  const create = () =>
    run(async () => {
      const errs: string[] = [];
      if (!TAG_RE.test(f.tag)) errs.push('Tag: 2–40 lowercase letters, digits or "-".');
      if (!f.name.trim()) errs.push('Name: required.');
      const m = parseDollarsToCents(f.bm);
      const a = f.ba.trim() ? parseDollarsToCents(f.ba) : m;
      if (!m.ok) errs.push(`Monthly bounty: ${m.error}`);
      if (!a.ok) errs.push(`Annual bounty: ${a.error}`);
      const termErr = f.dm ? monthlyTermError(offers, Number(f.dm), Number(f.dmm)) : null;
      if (termErr) errs.push(termErr);
      if (house) {
        // A Coach Cedar tag has no bounty (POST /api/admin/codes/house/tags has no bounty field).
        const houseErrs = errs.filter((e) => !e.startsWith('Monthly bounty') && !e.startsWith('Annual bounty'));
        if (houseErrs.length) { setNotice({ kind: 'error', text: 'Fix these first:', extra: houseErrs }); return; }
        try {
          const res = await CodesAdmin.createHouseTag(houseTagBody(f));
          onCreated(res.tag.id);
        } catch (e) {
          setNotice({ kind: 'error', text: errText(e) });
        }
        return;
      }
      if (errs.length || !m.ok || !a.ok) { setNotice({ kind: 'error', text: 'Fix these first:', extra: errs }); return; }
      const body: Record<string, unknown> = { tag: f.tag, name: f.name.trim(), currency: 'CAD', trial_days: f.trial,
        bounty_monthly_cents: m.cents, bounty_annual_cents: a.cents };
      if (f.dm) {
        body.discount_monthly_pct = Number(f.dm);
        body.discount_monthly_months = Number(f.dmm);
      }
      if (f.da) body.discount_annual_pct = Number(f.da);
      try {
        const res = await PartnersAdmin.createTag(partnerId, body);
        onCreated(res.tag.id);
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  return (
    <AdminCard>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <div style={row}>
        <div>
          <label style={label} htmlFor="ntf-tag">Tag (the /go link — cannot be renamed)</label>
          <input id="ntf-tag" style={input} value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value.toLowerCase() })} />
        </div>
        <div>
          <label style={label} htmlFor="ntf-name">Name</label>
          <input id="ntf-name" style={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </div>
        {!house && (
          <div>
            <label style={label} htmlFor="ntf-bm">Bounty monthly (CAD)</label>
            <input id="ntf-bm" style={{ ...input, width: 100 }} value={f.bm} onChange={(e) => setF({ ...f, bm: e.target.value })} />
          </div>
        )}
        {!house && (
          <div>
            <label style={label} htmlFor="ntf-ba">Bounty annual (CAD)</label>
            <input id="ntf-ba" style={{ ...input, width: 100 }} value={f.ba} placeholder="same" onChange={(e) => setF({ ...f, ba: e.target.value })} />
          </div>
        )}
        <div>
          <label style={label} htmlFor="ntf-trial">Trial</label>
          <select id="ntf-trial" style={input} value={f.trial} onChange={(e) => setF({ ...f, trial: Number(e.target.value) })}>
            {TRIAL_CHOICES.map((d) => <option key={d} value={d}>{trialLabel(d)}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="ntf-dm">Discount monthly</label>
          <select id="ntf-dm" style={input} value={f.dm}
            onChange={(e) => setF({ ...f, dm: e.target.value,
              // A new level re-picks its length: never a stale one from the level before (#4217 N3).
              dmm: String(defaultMonthlyTerm(offers, e.target.value ? Number(e.target.value) : null)) })}>
            <option value="">None</option>
            {choices.map((p) => <option key={p} value={p}>{p}%</option>)}
          </select>
        </div>
        {f.dm && (
          <div>
            <label style={label} htmlFor="ntf-dmm">Monthly discount lasts</label>
            <select id="ntf-dmm" style={input} value={f.dmm} onChange={(e) => setF({ ...f, dmm: e.target.value })}>
              {newTermChoices.map((m) => <option key={m} value={m}>the first {termChoiceLabel(m)}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={label} htmlFor="ntf-da">Discount annual (first year)</label>
          <select id="ntf-da" style={input} value={f.da} onChange={(e) => setF({ ...f, da: e.target.value })}>
            <option value="">None</option>
            {choices.map((p) => <option key={p} value={p}>{p}%</option>)}
          </select>
        </div>
        <AdminButton size="sm" onClick={create} disabled={busy}>Create tag</AdminButton>
      </div>
      <CoverageWarningLines testId="new-tag-coverage-warning"
        lines={offerCoverageWarnings(coverage, f.dm ? Number(f.dm) : null, f.dm ? Number(f.dmm) : null, f.da ? Number(f.da) : null)} />
      <ShortTermNotice months={f.dm ? Number(f.dmm) : null} testId="new-tag-short-term-notice" />
    </AdminCard>
  );
};

// ── one tag ──────────────────────────────────────────────────────────────────

const TagDetail: React.FC<{
  tag: TagRow;
  partnerId: string;
  partnerCode: string;
  tags: TagRow[];
  links: LinkRow[];
  levels: DiscountLevel[];
  offers: DiscountOffer[];
  coverage: DiscountCoverage | undefined;
  writable: boolean;
  endedTerminal: boolean;
  partnerStatus: PartnerStatus;
  judgedAtTap: boolean | null;
  onChanged: () => void;
  house?: boolean;
  partnerName: string;
}> = ({ tag, partnerId, partnerCode, tags, links, levels, offers, coverage, writable, endedTerminal, partnerStatus, judgedAtTap, onChanged, house = false, partnerName }) => {
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const [name, setName] = useState(tag.name);
  const [clipboard, setClipboard] = useState(tag.clipboard_handoff);
  const [offer, setOffer] = useState<OfferForm>(offerFormFromTag(tag));
  const [dests, setDests] = useState<string[]>(links.map((l) => l.destination));
  const [pendingStatus, setPendingStatus] = useState<PartnerStatus | null>(null);
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [codesKey, setCodesKey] = useState(0);

  useEffect(() => {
    PartnersAdmin.funnel(partnerId, 30, tag.id).then(setFunnel).catch(() => setFunnel(null));
  }, [partnerId, tag.id]);

  const offerDiff = useMemo(() => offerPatch(tag, offer, levels, offers), [tag, offer, levels, offers]);
  const mChoices = discountChoices(levels, tag.discount_monthly_pct);
  const aChoices = discountChoices(levels, tag.discount_annual_pct);
  const termChoices = monthlyTermChoices(offers, offer.discount_monthly_pct,
    { pct: tag.discount_monthly_pct ?? null, months: tag.discount_monthly_months ?? null });
  // An ended tag promises nothing any more; a live one's offer (as edited) is checked against the stores.
  const coverageLines = tag.status === 'ended' ? [] : offerCoverageWarnings(coverage, offer.discount_monthly_pct,
    offer.discount_monthly_pct != null ? offer.discount_monthly_months : null, offer.discount_annual_pct);
  const ended = tag.status === 'ended';
  // An ended tag, or any tag of an ended partner: its offer, links and codes are frozen.
  const editable = writable && tagEditable(tag.status, partnerStatus);

  const saveTag = () =>
    run(async () => {
      const body: Record<string, unknown> = {};
      if (name.trim() !== tag.name) body.name = name.trim();
      if (clipboard !== tag.clipboard_handoff) body.clipboard_handoff = clipboard;
      if (!Object.keys(body).length) return;
      try {
        const res = await PartnersAdmin.updateTag(tag.id, body);
        setNotice({ kind: 'ok', text: `Saved. Changed: ${Object.keys(res.changed || {}).join(', ') || 'nothing'}.`, extra: Object.values(res.notes || {}) });
        onChanged();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const saveOffer = () =>
    run(async () => {
      if (Object.keys(offerDiff.errors).length) { setNotice({ kind: 'error', text: 'Not saved:', extra: Object.values(offerDiff.errors) }); return; }
      if (!Object.keys(offerDiff.body).length) { setNotice({ kind: 'info', text: 'Nothing changed.' }); return; }
      try {
        const res = await PartnersAdmin.setOffer(tag.id, offerDiff.body);
        setNotice({
          kind: 'ok',
          text: `Offer now: ${res.tag.offer_summary}. Effective from ${res.effective_from ? new Date(res.effective_from).toLocaleString() : 'now'}.`,
          extra: Object.values(res.notes || {}),
        });
        onChanged();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const buildLinks = () =>
    run(async () => {
      try {
        await PartnersAdmin.buildLinks(tag.id, dests);
        setNotice({ kind: 'ok', text: 'Links built.' });
        onChanged();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const moveStatus = () =>
    run(async () => {
      if (!pendingStatus) return;
      try {
        await PartnersAdmin.setTagStatus(tag.id, pendingStatus);
        setNotice({ kind: 'ok', text: `Tag is now ${pendingStatus}.` });
        setPendingStatus(null);
        onChanged();
      } catch (e) {
        setPendingStatus(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminCard>
        <div style={{ ...row, justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>{tag.tag} <StatusBadge status={tag.status} tone={statusBadge(tag.status)} /></h3>
            <div style={hint}>{tag.offer_summary}{house ? ' · Coach Cedar code: no bounty' : ` · ${bountyLabel(tag)}`}</div>
          </div>
          <div style={{ ...row, gap: 6 }}>
            <code style={{ fontSize: 13 }}>{goLink(tag.tag)}</code>
            <AdminButton size="sm" variant="ghost" onClick={() => copyText(goLink(tag.tag))}>Copy link</AdminButton>
          </div>
        </div>
        <NoticeLine notice={notice} onClose={() => setNotice(null)} />

        {funnel && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '8px 0', fontSize: 13 }} title={funnel.notes?.window}>
            {FUNNEL_STAGES.map((s) => (
              <div key={s}>
                <div style={hint}>{FUNNEL_STAGE_LABELS[s]}</div><b>{funnel.totals[s] ?? 0}</b>
                {/* Accounts without a click are part of Accounts (S1 #5418): a breakdown, never a stage. */}
                {s === 'accounts' && accountsWithoutClickText(funnel.totals.accounts_without_click) && (
                  <div style={hint}>{accountsWithoutClickText(funnel.totals.accounts_without_click)}</div>
                )}
              </div>
            ))}
            <div><div style={hint}>Accrued</div><b>{formatCents(funnel.totals.accrued_cents)}</b></div>
            <div style={hint}>last {funnel.window_days} days, counted by click date (accounts without a click: when attributed)</div>
          </div>
        )}

        {writable && (
          <div style={{ ...row, marginTop: 8 }}>
            {tagStatusMoves(tag.status, partnerStatus, endedTerminal).map((s) => (
              <AdminButton key={s} size="sm" variant={s === 'ended' ? 'danger' : 'secondary'} onClick={() => setPendingStatus(s)} disabled={busy}>
                {s === 'active' ? 'Resume' : s === 'paused' ? 'Pause' : 'End tag'}
              </AdminButton>
            ))}
            {ended && endedTerminal && <span style={hint}>Ended is final — this tag cannot be reactivated.</span>}
            {partnerStatus === 'ended' && <span style={hint}>The partner has ended: its tags follow it and can no longer be paused, resumed or ended.</span>}
          </div>
        )}
      </AdminCard>

      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Links</h4>
        {links.length === 0 && <div style={hint}>No links built yet.</div>}
        {links.map((l) => (
          <div key={l.id} style={{ ...row, marginBottom: 4 }}>
            <span style={{ ...hint, width: 70 }}>{l.destination}</span>
            <code style={{ fontSize: 13 }}>{l.url}</code>
            <AdminButton size="sm" variant="ghost" onClick={() => copyText(l.url)}>Copy</AdminButton>
          </div>
        ))}
        {editable && (
          <div style={{ ...row, marginTop: 8 }}>
            {LINK_DESTINATIONS.map((d) => (
              <label key={d} style={{ fontSize: 13 }}>
                <input type="checkbox" checked={dests.includes(d)} onChange={(e) => setDests((cur) => (e.target.checked ? [...cur, d] : cur.filter((x) => x !== d)))} />{' '}
                {d === 'with' ? `co-branded page (/with/${partnerCode.toLowerCase()})` : d}
              </label>
            ))}
            <AdminButton size="sm" variant="secondary" onClick={buildLinks} disabled={busy || !dests.length}>Build / refresh links</AdminButton>
          </div>
        )}
      </AdminCard>

      {/* Hides itself once the tag or partner has ended (tagEditable). */}
      <PartnerWebsiteCode tag={tag} links={links} partnerStatus={partnerStatus} />

      {/* The partner's own email (UC3): the shared code, the offer, the store links, the redeem steps.
          Not for Coach Cedar's own codes. Re-reads the shared code when one is made below. */}
      {!house && <PartnerEmailKit tag={tag} partnerName={partnerName} partnerStatus={partnerStatus} refreshKey={codesKey} />}

      {/* The partner webhook intake (owner rulings 2026-10-21): review first or send automatically, and
          the webhook a registration platform posts people to. Not for Coach Cedar's own codes. */}
      {!house && <PartnerTagSendMode partnerId={partnerId} tag={tag} partnerStatus={partnerStatus} writable={writable} onChanged={onChanged} />}
      {!house && <PartnerWebhookCard tag={tag} partnerName={partnerName} partnerStatus={partnerStatus} writable={writable} />}

      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>{house ? 'Offer' : 'Offer and bounties'}</h4>
        <div style={row}>
          <div>
            <label style={label} htmlFor="of-trial">Free trial</label>
            <select id="of-trial" style={input} disabled={!editable} value={offer.trial_days} onChange={(e) => setOffer({ ...offer, trial_days: Number(e.target.value) })}>
              {TRIAL_CHOICES.map((d) => <option key={d} value={d}>{trialLabel(d)}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="of-dm">Discount, monthly plan</label>
            <select id="of-dm" style={input} disabled={!editable} value={offer.discount_monthly_pct ?? ''}
              onChange={(e) => setOffer({ ...offer, discount_monthly_pct: e.target.value === '' ? null : Number(e.target.value) })}>
              <option value="">None</option>
              {mChoices.map((p) => <option key={p} value={p}>{p}% off{levels.find((l) => l.pct === p)?.status === 'retired' ? ' (retired — kept)' : ''}</option>)}
            </select>
          </div>
          {offer.discount_monthly_pct != null && (
            <div>
              <label style={label} htmlFor="of-dmm">Monthly discount lasts</label>
              <select id="of-dmm" style={input} disabled={!editable} value={offer.discount_monthly_months}
                onChange={(e) => setOffer({ ...offer, discount_monthly_months: Number(e.target.value) })}>
                {(termChoices.length ? termChoices : [offer.discount_monthly_months]).map((m) => (
                  <option key={m} value={m}>the first {termChoiceLabel(m)}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={label} htmlFor="of-da">Discount, annual plan (first year)</label>
            <select id="of-da" style={input} disabled={!editable} value={offer.discount_annual_pct ?? ''}
              onChange={(e) => setOffer({ ...offer, discount_annual_pct: e.target.value === '' ? null : Number(e.target.value) })}>
              <option value="">None</option>
              {aChoices.map((p) => <option key={p} value={p}>{p}% off{levels.find((l) => l.pct === p)?.status === 'retired' ? ' (retired — kept)' : ''}</option>)}
            </select>
          </div>
          {!house && (
            <div>
              <label style={label} htmlFor="of-bm">Bounty, monthly subscriber (CAD)</label>
              <input id="of-bm" style={{ ...input, width: 110 }} disabled={!editable} value={offer.bounty_monthly} onChange={(e) => setOffer({ ...offer, bounty_monthly: e.target.value })} />
            </div>
          )}
          {!house && (
            <div>
              <label style={label} htmlFor="of-ba">Bounty, annual subscriber (CAD)</label>
              <input id="of-ba" style={{ ...input, width: 110 }} disabled={!editable} value={offer.bounty_annual} onChange={(e) => setOffer({ ...offer, bounty_annual: e.target.value })} />
            </div>
          )}
          {editable && <AdminButton size="sm" onClick={saveOffer} disabled={busy || !Object.keys(offerDiff.body).length}>Save offer</AdminButton>}
        </div>
        {Object.values(offerDiff.errors).map((e) => <div key={e} style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{e}</div>)}
        <CoverageWarningLines testId="tag-coverage-warning" lines={coverageLines} />
        <ShortTermNotice months={offer.discount_monthly_pct != null ? offer.discount_monthly_months : null}
          testId="tag-short-term-notice" />
        <div style={hint}>
          Trial is 7 days, 2 weeks or 1 month only. A monthly discount lasts the first 1, 3, 6 or 12 months (you pick); an annual discount lasts the first year. Each discount and length needs a live offer in both stores (Store access) before anyone can buy with it. Offers apply to redemptions from now on. {house ? 'A Coach Cedar code never pays a bounty.' : BOUNTY_FORWARD_NOTE}
        </div>
      </AdminCard>

      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Tag settings</h4>
        <div style={row}>
          <div>
            <label style={label} htmlFor="tg-name">Name</label>
            <input id="tg-name" style={input} disabled={!writable} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" disabled={!writable} checked={clipboard} onChange={(e) => setClipboard(e.target.checked)} /> Clipboard handoff
          </label>
          {writable && <AdminButton size="sm" variant="secondary" onClick={saveTag} disabled={busy || (name.trim() === tag.name && clipboard === tag.clipboard_handoff)}>Save</AdminButton>}
        </div>
        <div style={hint}>{CLIPBOARD_HANDOFF_NOTE}</div>
      </AdminCard>

      {editable && <TagCodeMaker tag={tag} onMade={() => setCodesKey((k) => k + 1)} />}
      {writable && !editable && (
        <div style={hint}>This tag {ended ? 'has ended' : "belongs to a partner that has ended"}: its offer, links and codes can no longer change. Its existing codes can still be revoked below.</div>
      )}
      <PartnerCodesPanel partnerId={partnerId} tags={tags} fixedTagId={tag.id} writable={writable} refreshKey={codesKey} />

      {pendingStatus === 'ended' && (
        <TypedConfirm
          title={`End tag ${tag.tag}?`}
          body={statusMoveCopy('ended', 'tag', judgedAtTap)}
          expected={tag.tag}
          confirmLabel="End tag"
          busy={busy}
          onConfirm={moveStatus}
          onCancel={() => setPendingStatus(null)}
        />
      )}
      {pendingStatus && pendingStatus !== 'ended' && (
        <TypedConfirm
          title={`${pendingStatus === 'paused' ? 'Pause' : 'Resume'} tag ${tag.tag}?`}
          body={statusMoveCopy(pendingStatus, 'tag', judgedAtTap)}
          expected={pendingStatus === 'paused' ? 'PAUSE' : 'RESUME'}
          confirmLabel={pendingStatus === 'paused' ? 'Pause' : 'Resume'}
          danger={false}
          busy={busy}
          onConfirm={moveStatus}
          onCancel={() => setPendingStatus(null)}
        />
      )}
    </div>
  );
};
